import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { GACodes, LISTING_HEAP_IDS, LISTING_HEAP_SOURCE } from '@/src/listings/components/_shared/constants';

import { MANY_COLLECTIONS_LIMIT } from './constants';

import styles from './CreatorCollectionsLink.module.scss';

export interface CreatorCollectionsLinkProps {
  collections: WebappResponse['collections'];
  viewMoreCollections?: (isOpen: boolean) => void;
}

export const CreatorCollectionsLink: React.FC<CreatorCollectionsLinkProps> = ({ collections }) => {
  const { formatMessage } = useIntl();
  const normalizedCollections = Array.isArray(collections) ? collections : [];
  const count = normalizedCollections.length;
  const singleCollection = normalizedCollections[0];

  useEffect(() => {
    const link = document.getElementById('link-to-creator-collections');
    const _handleSingleCollectionClick = () => {
      _trackCollectionLink(GACodes.GA_ACTION_COLLECTION_PAGE_LINK_CLICK);
    };

    link?.addEventListener('click', _handleSingleCollectionClick);

    return () => {
      link?.removeEventListener('click', _handleSingleCollectionClick);
    };
  }, [singleCollection]);

  if (count === 0) {
    return <></>;
  }

  let message = null;

  const _trackCollectionLink = (action: string) => {
    trackAnalyticsEvent({
      category: GACodes.GA_CATEGORY_CREATOR_COLLECTIONS_LISTING,
      action,
      label: GACodes.GA_LABEL_EVENT_DETAILS,
      dimensions: {
        source: LISTING_HEAP_SOURCE,
      },
    });
  };

  const _handleMultipleCollectionClick = () => {
    // TODO EB-312460: handle multiple collections
    _trackCollectionLink(GACodes.GA_ACTION_COLLECTION_PAGE_LINK_CLICK);
    // until we support multiple collections, we'll open the first one. 1 collections only represents 90% of the collections
    window.open(singleCollection.collectionUrl, '_blank');
  };

  if (count === 1) {
    const { name, collectionUrl } = normalizedCollections[0];
    message = (
      <FormattedMessage
        id="Part of the <link>{name}</link> collection"
        defaultMessage="Part of the <link>{name}</link> collection"
        values={{
          name,
          link: (chunks) => (
            <a
              id="link-to-creator-collections"
              data-heap-id="Hero Content - Part of /Featured in Collection/s button"
              href={collectionUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </a>
          ),
        }}
      />
    );
  } else {
    const countToShow =
      count > MANY_COLLECTIONS_LIMIT
        ? formatMessage(
            { id: '{maxCount}+', defaultMessage: '{maxCount}+', description: 'Truncated count when too many, ex: 9+' },
            { maxCount: MANY_COLLECTIONS_LIMIT },
          )
        : `${count}`;
    message = (
      <FormattedMessage
        id="Featured in <button>{count} collections</button>"
        defaultMessage="Featured in <button>{count} collections</button>"
        values={{
          count: countToShow,
          button: (chunks) => (
            <button
              className="creator-collections-link__button"
              onClick={_handleMultipleCollectionClick}
              data-heap-id={LISTING_HEAP_IDS.PART_OF_A_COLLECTION}
            >
              {chunks}
            </button>
          ),
        }}
      />
    );
  }

  return (
    <div data-testid="link-to-creator-collections" className={styles.root}>
      {message}
    </div>
  );
};
